import {createAsyncThunk} from "@reduxjs/toolkit";
import {AxiosError} from "axios";
import {removeNulls} from "ndr-designsystem";
import Tenant from "../../../api/fixed/Tenant"
import {RootState} from "../../../app/store";
import TenantUser from "../../../api/fixed/TenantUser";
import {IEcertificate} from "../../../api/fixed/Certificate";
import {getDomainAdminService} from "../../../api/generated/domainAdmin";
import type {
    CertificateType as GeneratedCertificateType,
    IGetTenantByIdTenantNodes,
    IGetTenantsTenantNodes,
    IGetUsersByGroupGroupsUsers
} from "../../../api/generated/models";

const api = getDomainAdminService();

// Ids are opaque strings end to end - the values exceed the safe integer range, so
// parsing a number from one would silently corrupt it.
type TenantNode = IGetTenantsTenantNodes | IGetTenantByIdTenantNodes;

// Maps generated tenant node (_id/_rowVersion) to the internal Tenant shape
function mapTenant(t: TenantNode): Tenant {
    /* eslint-disable no-underscore-dangle */
    const node = t as IGetTenantsTenantNodes;
    return removeNulls({
        ...node,
        inventoryItemId: node._id ?? '',
        revision: node._rowVersion ?? 0,
        mainContact: node.mainContact ? {
            ...node.mainContact,
            inventoryItemId: node.mainContact._id ?? '',
            revision: node.mainContact._rowVersion ?? 0,
        } : undefined,
        callRetryDelays: node.callRetryDelays?.map(Number) ?? [],
        controllableResourcesCount: node._controllableResourcesIds?.length ?? 0,
        activationsCount: 0,
    });
    /* eslint-enable no-underscore-dangle */
}

// The users endpoints speak the identity-provider user shape, not the internal one
function mapTenantUser(user: IGetUsersByGroupGroupsUsers): TenantUser {
    return removeNulls({
        inventoryItemId: user.id ?? user.userId ?? undefined,
        name: user.username ?? undefined,
        email: user.eMail ?? undefined,
    });
}

// The endpoint returns the complete list; paging happens client-side in the grid
export const fetchTenants = createAsyncThunk(
    'TenantList/fetch',
    async () => {
        const response = await api.getTenants();
        return response.map(mapTenant);
    }
);

export const fetchTenantByID = createAsyncThunk('TenantList/fetchByID',
    async (id: string) => {
        const [tenantResponse, usersResponse] = await Promise.all([
            api.getTenantsId(id),
            api.getTenantsTenantIdUsers(id),
        ]);
        const tenant = mapTenant(tenantResponse);
        tenant.users = usersResponse.map(mapTenantUser);
        return tenant;
    })


export const editTenantActivation = createAsyncThunk(
    'TenantActivation/edit',
    async (tenantActiveState: { id: string, revision: number, active: boolean }) => {
        const response = await api.putTenantsIdRevisionActive(
            tenantActiveState.id,
            tenantActiveState.revision,
            tenantActiveState.active
        );
        const item = response[0];
        return {
            inventoryItemId: tenantActiveState.id,
            // eslint-disable-next-line no-underscore-dangle
            revision: item?._rowVersion ?? tenantActiveState.revision,
            activeState: tenantActiveState.active,
        }
    }
);

export const AddTenant = createAsyncThunk("Tenant/add",
    async (details: Tenant) => {
        const response = await api.postTenants({
            name: details.name ?? '',
            address: details.address ?? '',
            subdomain: details.subdomain ?? '',
            marketPartnerIDCode: details.marketPartnerIDCode ?? '',
            marketPartnerIDEncoding: details.marketPartnerIDEncoding ?? '',
            onCallServiceEmail: details.onCallServiceEmail ?? '',
            onCallServicePhone: details.onCallServicePhone ?? '',
            callRetryDelays: (details.callRetryDelays ?? []).map(String),
            useAvatForActivations: details.useAvatForActivations,
            exPostRecipients: details.exPostRecipients ?? [],
            adminNotificationsRecipients: details.adminNotificationsRecipients ?? [],
            mainContact: {
                name: details.mainContact?.name ?? '',
                email: details.mainContact?.email ?? '',
                phone: details.mainContact?.phone ?? '',
            },
        });
        return {
            ...details,
            // eslint-disable-next-line no-underscore-dangle
            inventoryItemId: response._id ?? '',
            // eslint-disable-next-line no-underscore-dangle
            revision: response._rowVersion ?? 0,
            users: [],
            isActive: true,
            controllableResourcesCount: 0,
            activationsCount: 0,
        } as Tenant
    })

export const editTenantDetails = createAsyncThunk('TenantDetails/edit',
    async (newDetails: Tenant) => {
        const response = await api.putTenantsId(newDetails.inventoryItemId ?? null, {
            inventoryItemId: newDetails.inventoryItemId,
            revision: newDetails.revision,
            name: newDetails.name,
            address: newDetails.address,
            onCallServiceEmail: newDetails.onCallServiceEmail,
            onCallServicePhone: newDetails.onCallServicePhone,
            callRetryDelays: newDetails.callRetryDelays?.map(String),
            useAvatForActivations: newDetails.useAvatForActivations,
            exPostRecipients: newDetails.exPostRecipients,
            adminNotificationsRecipients: newDetails.adminNotificationsRecipients,
            mainContact: newDetails.mainContact ? {
                inventoryItemId: newDetails.mainContact.inventoryItemId,
                name: newDetails.mainContact.name,
                email: newDetails.mainContact.email,
                phone: newDetails.mainContact.phone,
                revision: newDetails.mainContact.revision,
            } : undefined,
        });
        const { revision, inventoryItemId, ...rest } = newDetails
        return {
            ...rest,
            inventoryItemId,
            revision: response.tenant?.revision ?? revision,
        } as Tenant
    })

export const AddTenantUser = createAsyncThunk("TenantUser/add",
    async (details: {
        userId: string, tenantId: string | undefined, tenantRevision: number | undefined
    }, thunkAPI) => {
        // details.userId is the identity provider's user id (see AddInputs)
        const response = await api.postTenantsTenantIdUsers(details.tenantId ?? null, {
            providerUserId: details.userId,
            tenantRevision: details.tenantRevision,
        });
        const addedUserId = response.addUsersToGroups?.users?.[0]?.userId ?? details.userId;

        // The endpoint returns ids only, so fill in the display fields from the
        // available-users list that the dialog was populated from
        const state: RootState = thunkAPI.getState() as RootState;
        const availableUser = state.Admins.users?.find((u) => u.providerUserId === details.userId);

        return {
            user: removeNulls({
                inventoryItemId: addedUserId,
                name: availableUser?.username ?? undefined,
                email: availableUser?.eMail ?? undefined,
            }) as TenantUser,
            tenant: {
                inventoryItemId: details.tenantId,
                // the response carries no new tenant revision
                revision: details.tenantRevision,
            }
        }
    })

export const EditTenantUser = createAsyncThunk("TenantUser/edit",
    async (details: TenantUser, thunkAPI) => {
        const state: RootState = thunkAPI.getState() as RootState;
        const user = state.Tenants.currentTenant?.users?.find((item) =>
            item.inventoryItemId === details.inventoryItemId &&
            item.name === details.name &&
            item.phone === details.phone &&
            item.email === details.email
        )
        if (user)
            return { ...details }

        const response = await api.putPersonsId(details.inventoryItemId ?? null, {
            inventoryItemId: details.inventoryItemId,
            name: details.name,
            email: details.email,
            phone: details.phone,
            revision: details.revision,
        });
        const { inventoryItemId, revision, ...mainDetails } = details
        return { ...mainDetails, ...response }
    })

export const DeleteTenantUser = createAsyncThunk('TenantUser/delete',
    async (details: TenantUser, thunkAPI) => {
        const state: RootState = thunkAPI.getState() as RootState;
        await api.deleteTenantsTenantIdUsersUserId(
            state.Tenants.currentTenant?.inventoryItemId ?? null,
            details.inventoryItemId ?? null
        );
        return details.inventoryItemId
    })

export const ImportCertificate = createAsyncThunk('Certificate/import',
    async (details: IEcertificate, {rejectWithValue}) => {
        try {
            const formData = {
                File: details.CF ?? null,
                TenantRevision: details.revision,
                Password: details.password ?? null,
                PublicKey: details.publicKey ?? null,
                PrivateKey: details.privateKey ?? null,
            };
            const tenantId = details.tenantID ?? null;
            let response;
            if (details.type === 'smime-decryption') {
                response = await api.putTenantsTenantIdCertificatesSmimeDecryption(tenantId, formData);
            } else if (details.type === 'smime-encryption') {
                response = await api.putTenantsTenantIdCertificatesSmimeEncryption(tenantId, formData);
            } else {
                response = await api.putTenantsTenantIdCertificatesClientAuth(tenantId, formData);
            }
            return { data: response, type: details.type }
        } catch (e) {
            const error = e as AxiosError
            return rejectWithValue(error.response!.data)
        }
    })

export const fetchCertificate = createAsyncThunk('Certificate/fetch',
    async (details: IEcertificate) => {
        // The certificate type is an enum discriminator, not an id
        const certificateType = Number(details.CT) as GeneratedCertificateType;
        const response = await api.getTenantsTenantIdCertificateCertificateType(
            details.tenantID ?? null,
            certificateType
        );
        return { date: response.uploadedDate, type: details.CT }
    })

export const repairTenant = createAsyncThunk('Tenant/repair',
    async (id: string, thunkAPI) => {
        const response = await api.postTenantsIdRepair(id);
        thunkAPI.dispatch(fetchTenantByID(id));
        return response;
    })